// @tennis-matches/services/i18n
// Remote i18n service
// Migrated from src/i18n/remoteI18n.service.js

import { dbRepository } from "../supabase/index.js";
import { getRemoteMessagesVersion } from "./i18n.js";

// ------------------------------------------------------------
// LocalStorage cache (fast start, avoids "flash of keys")
// ------------------------------------------------------------
const __LS_KEY_PREFIX__ = "ttb:i18n:";
const __LS_VERSION__ = 1;

function __lsKey(locale) {
  return `${__LS_KEY_PREFIX__}${String(locale || "").trim()}`;
}

function __isPlainObject(v) {
  return !!v && typeof v === "object" && !Array.isArray(v);
}

function __readLocalCache(locale) {
  try {
    if (typeof globalThis === "undefined") return {};
    const ls = globalThis.localStorage;
    if (!ls) return {};
    const raw = ls.getItem(__lsKey(locale));
    if (!raw) return {};

    const parsed = JSON.parse(raw);

    // Format A (preferred): { v, ts, msgs }
    if (__isPlainObject(parsed) && __isPlainObject(parsed.msgs)) {
      return parsed.msgs;
    }

    // Format B (legacy): plain object of messages
    if (__isPlainObject(parsed)) {
      return parsed;
    }

    return {};
  } catch (_) {
    return {};
  }
}

function __writeLocalCache(locale, msgs) {
  try {
    if (typeof globalThis === "undefined") return;
    const ls = globalThis.localStorage;
    if (!ls) return;
    if (!__isPlainObject(msgs) || Object.keys(msgs).length === 0) return;

    const payload = {
      v: __LS_VERSION__,
      ts: Date.now(),
      msgs,
    };
    ls.setItem(__lsKey(locale), JSON.stringify(payload));
  } catch (_) {
    // ignore (quota, private mode, etc.)
  }
}

/**
 * Synchronous fast-path: load cached remote messages from localStorage.
 * Intended for app bootstrap to avoid showing raw keys.
 */
export function loadCachedI18nMessages(locale) {
  const loc = String(locale || "").trim();
  if (!loc) return {};

  const cached = __readLocalCache(loc);
  if (__isPlainObject(cached) && Object.keys(cached).length > 0) {
    // also seed memory cache for this session
    __i18n_cache__.set(loc, cached);
    return cached;
  }
  return {};
}

// ------------------------------------------------------------
// Singleflight + Cache (per locale)
// ------------------------------------------------------------
const __i18n_singleflight__ = new Map(); // locale -> Promise<messages>
const __i18n_cache__ = new Map();        // locale -> messages
let __i18nAbortWarned = false;
let __initRemoteI18nInflight = null;
let __initRemoteI18nLocale = "";

function __isAbortError(err) {
  const name = String(err?.name || "").toLowerCase();
  const msg = String(err?.message || "").toLowerCase();
  return name === "aborterror" || msg.includes("aborted");
}

function __getCachedMessages(locale) {
  const loc = String(locale || "").trim();
  if (!loc) {
    console.warn("[i18n] No locale provided to __getCachedMessages");
    return {};
  }

  // memory cache first (session)
  const cached = __i18n_cache__.get(loc);
  if (cached) {
    return cached;
  }

  // localStorage cache (fast start)
  const lsCached = __readLocalCache(loc);
  if (__isPlainObject(lsCached) && Object.keys(lsCached).length > 0) {
    __i18n_cache__.set(loc, lsCached);
    return lsCached;
  }

  return {};
}

async function __loadMessagesFromDbSingleflight(locale) {
  const loc = String(locale || "").trim();
  if (!loc) return {};

  // singleflight
  let p = __i18n_singleflight__.get(loc);
  if (!p) {
    p = (async () => {
      const __dev =
        typeof import.meta !== "undefined" && !!import.meta.env?.DEV && typeof performance !== "undefined";
      const __i18nMark = __dev
        ? `tm-i18n-db-load-${loc}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
        : "";
      if (__dev) {
        performance.mark(`${__i18nMark}:start`);
      }
      console.log(`[i18n] Loading messages from DB for locale: "${loc}"`);
      const t0 = __dev ? performance.now() : 0;
      const m = await dbRepository.getI18nMessages(loc);
      if (__dev) {
        performance.mark(`${__i18nMark}:end`);
        try {
          performance.measure(__i18nMark, `${__i18nMark}:start`, `${__i18nMark}:end`);
        } catch (_) {}
        const ms = performance.now() - t0;
        const keyCount = m && typeof m === "object" ? Object.keys(m).length : 0;
        console.debug(`[perf] i18n remote DB getI18nMessages(${loc})`, { ms: Math.round(ms * 10) / 10, keyCount });
      }
      const out = (m && typeof m === "object") ? m : {};
      const outCount = Object.keys(out).length;
      if (outCount > 0) {
        console.log(`[i18n] Caching ${outCount} messages for locale: "${loc}"`);
        __i18n_cache__.set(loc, out);
        __writeLocalCache(loc, out);
      }
      return out;
    })().finally(() => {
      __i18n_singleflight__.delete(loc);
    });
    __i18n_singleflight__.set(loc, p);
  }
  return await p;
}

/**
 * Loads i18n messages from DB and pushes them into i18n runtime.
 * app.js stays DB-agnostic; dbRepository usage lives here.
 */
export async function initRemoteI18n({ getState, setRemoteMessages, requestRender } = {}) {
  const st = typeof getState === "function" ? getState() : null;
  const rawLocale = String(st?.app?.locale || "de-DE").trim() || "de-DE";
  const locale = (() => {
    const s = rawLocale.replaceAll("_", "-");
    const parts = s.split("-").filter(Boolean);
    if (!parts.length) return "de-DE";
    const lang = String(parts[0] || "de").toLowerCase();
    const region = parts[1] ? String(parts[1]).toUpperCase() : "";
    return region ? `${lang}-${region}` : lang === "de" ? "de-DE" : lang;
  })();

  if (__initRemoteI18nInflight && __initRemoteI18nLocale === locale) {
    return __initRemoteI18nInflight;
  }

  __initRemoteI18nLocale = locale;
  __initRemoteI18nInflight = __initRemoteI18nImpl({ getState, setRemoteMessages, requestRender, locale, rawLocale }).finally(
    () => {
      if (__initRemoteI18nInflight) {
        __initRemoteI18nInflight = null;
      }
    }
  );
  return __initRemoteI18nInflight;
}

async function __initRemoteI18nImpl({ getState, setRemoteMessages, requestRender, locale, rawLocale } = {}) {
  try {
    if (typeof dbRepository?.getI18nMessages !== "function") {
      console.warn("[i18n] Remote load skipped: dbRepository.getI18nMessages missing");
      return;
    }

    console.log(`[i18n] Resolved locale: "${rawLocale}" -> "${locale}"`);

    const alreadySeeded = getRemoteMessagesVersion() > 0;
    const cached = __getCachedMessages(locale);

    // Bootstrap hat Cache bereits gesetzt — kein redundantes setRemoteMessages + Re-Render
    if (!alreadySeeded && cached && typeof cached === "object" && Object.keys(cached).length > 0) {
      if (typeof setRemoteMessages === "function") setRemoteMessages(cached);
      if (typeof requestRender === "function") requestRender();
    }

    const msgs = await __loadMessagesFromDbSingleflight(locale);
    if (msgs && typeof msgs === "object" && Object.keys(msgs).length > 0) {
      if (typeof setRemoteMessages === "function") setRemoteMessages(msgs);
      if (typeof requestRender === "function") requestRender();
      console.info(`[i18n] ✅ Dynamische Texte aus DB geladen: ${Object.keys(msgs).length} Keys`);
    } else {
      console.warn("[i18n] ⚠️ No messages loaded from DB (keeping cached/code fallback)");
    }
  } catch (e) {
    if (__isAbortError(e)) {
      if (!__i18nAbortWarned) {
        __i18nAbortWarned = true;
        console.warn("[i18n] Remote load aborted/timeout (ignored, using cached/code fallback).");
      }
      return;
    }
    console.warn("[i18n] Remote load failed (ignored):", e);
  }
}
